import React, { Dispatch, SetStateAction } from "react";

export interface BadgeType {
  badgeText?: string | number;
  badgeColor?:
    | "light-danger"
    | "light-success"
    | "light-warning"
    | "light-info"
    | "light-primary"
    | "danger"
    | "success"
    | "warning"
    | "info"
    | "primary"
    | "secondary";
  className?: string;
  style?: React.CSSProperties;
  badgeTooltip?:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal;
}

export type TableTextType =
  | string
  | number
  | {
      text: string | number;
      textStyle?: any;
      textClassName?: string;
    }[];

export interface TableCellConfig {
  className?: string;
  style?: React.CSSProperties;
  colSpan?: number;
  rowSpan?: number;
}

export interface TableItemType {
  img?: string;
  title?: TableTextType;
  titleStyle?: any;
  titleClassName?: string;
  subtitle?: TableTextType;
  subtitleStyle?: any;
  subtitleClassName?: string;
  badges?: BadgeType[];
  config?: TableCellConfig;
  checkbox?: {
    id?: number | string;
    isSelected: boolean;
    isHidden?: boolean;
  };
}

export type TableExtraItemType = Omit<TableItemType, "checkbox">;

export type ExtraDataPreset =
  | "default"
  | "bordered"
  | "indented"
  | "highlighted"
  | "grouped";

export interface TableData {
  id: number | string;
  data: TableItemType[];
  rowClassName?: string;
  crudData?: any;
  extraData?: TableExtraItemType[][];
  extraDataPreset?: ExtraDataPreset;
}

export interface TableHeaderCategoryConfig {
  value: string;
  label?: string;
  minW?: number;
  maxW?: number;
  width?: number;
  withOrder?: boolean;
  withSearch?:
    | boolean
    | {
        placeholder?: string;
        withoutSearchIcon?: boolean;
        withoutSearchClear?: boolean;
        searchDelay?: number;
      };
  withFilter?: {
    value: string | number;
    label: string | number;
  }[];
  filterDefaultIdx?: number;
  withDatePicker?:
    | {
        minDate?: Date;
        maxDate?: Date;
        placeholder?: string;
        withClearData?: boolean;
      }
    | boolean;
}

export interface TablePaginationInterface {
  page: number;
  rowsPerPage: number;
}

export interface TableActions {
  tableSearch: any;
  tablePagination: TablePaginationInterface;
  globalFilterOptions: TableFilterDataItem[] | null;
  tableGlobalSearch: string;
  selectedItems: any[];
  tableContext: TableContextType;
}

export interface TableFilterDataItem {
  label: string;
  value: number;
}

export type TableSearchHandler = (
  key: string,
  value: string | number,
  options?: { resetPage?: boolean },
) => void;

export type TableContextType = {
  tableData: TableData[];
  setTableData: Dispatch<SetStateAction<TableData[]>>;
  tableHeader: TableHeaderCategoryConfig[];
  setTableHeader: (value: TableHeaderCategoryConfig[]) => void;
  tablePagination: { page: number; rowsPerPage: number };
  handleSetTablePagination: (
    callBack: (prev: { page: number; rowsPerPage: number }) => {
      page: number;
      rowsPerPage: number;
    },
  ) => void;
  tableSearch: any;
  setTableSearch: React.Dispatch<React.SetStateAction<object | null>>;
  handleTableSearch: TableSearchHandler;
  tableOrder: any;
  handleTableOrder: (key: string) => void;
  deleteItem: (value: string | number) => void;
  tableGlobalSearch: string;
  setTableGlobalSearch: (value: string) => void;
  tableGlobalFilter: string;
  setTableGlobalFilter: (value: string) => void;
  globalFilterOptions: TableFilterDataItem[] | null;
  setGlobalFilterOptions: Dispatch<
    SetStateAction<TableFilterDataItem[] | null>
  >;
  handleCheckboxClick: (
    crudData: object,
    tableItemId?: string | number,
    checkboxId?: string | number,
  ) => void;
  selectedItems: any[];
  setSelectedItems: Dispatch<SetStateAction<any[]>>;
  handleCheckAllItems: () => void;
  debouncedTableContextValues: any;
};

export const TableContext = React.createContext<TableContextType | undefined>(
  undefined,
);
